// src/pages/TrademarksPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit, Delete } from '@mui/icons-material';
import { trademarksApi, uploadFile } from '../services/api';
import { useTrademarks, useTrademarkMutations } from '../services/api';
import { resolveImageUrl } from '../utils/imageUrl';
import { CircularProgress } from '@mui/material';

const TrademarksPage = () => {
  const { data: trademarks = [], isLoading, error } = useTrademarks();
  const { 
    createMutation, 
    updateMutation, 
    deleteMutation 
  } = useTrademarkMutations();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameArabic: '',
    imageUrl: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });


  const handleOpen = (trademark = null) => {
    // Reset file input when opening the dialog
    setSelectedFile(null);
    if (trademark) {
      setFormData({
        name: trademark.name,
        nameArabic: trademark.nameArabic,
        imageUrl: resolveImageUrl(trademark.imageUrl),
      });
      setEditingId(trademark.id);
    } else {
      setFormData({ name: '', nameArabic: '', imageUrl: '' });
      setEditingId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFile(null);
    setIsUploading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

   const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Clear the URL field when a file is selected
      setFormData(prev => ({
        ...prev,
        imageUrl: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsUploading(true);
      
      if (!selectedFile && !formData.imageUrl) {
        throw new Error('Please upload an image');
      }

      const data = { ...formData };

      // Upload file if selected
      if (selectedFile) {
        const uploadResponse = await uploadFile(selectedFile);
        if (uploadResponse.status) {
          data.imageUrl = uploadResponse.data.url;
        } else {
          throw new Error(uploadResponse.message || 'Failed to upload image');
        }
      }
      
      if (data.imageUrl) {
        data.imageUrl = resolveImageUrl(data.imageUrl);
      }

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, ...data });
        showSnackbar('Trademark updated successfully');
      } else {
        await createMutation.mutateAsync(data);
        showSnackbar('Trademark created successfully');
      }
      handleClose();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'An error occurred';
      showSnackbar(message, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this trademark?')) {
      try {
        await deleteMutation.mutateAsync(id);
        showSnackbar('Trademark deleted successfully');
      } catch (error) {
        showSnackbar('Failed to delete trademark', 'error');
      }
    }
  };


  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
    if (isLoading) {
          return <LinearProgress />;
    }
  

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Trademarks</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Trademark
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Arabic Name</TableCell>
              <TableCell>Image URL</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trademarks.map((trademark) => (
              <TableRow key={trademark.id}>
                <TableCell>{trademark.name}</TableCell>
                <TableCell>{trademark.nameArabic}</TableCell>
                <TableCell>
                  {trademark.imageUrl && (
                    <a
                      href={resolveImageUrl(trademark.imageUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Image
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(trademark)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(trademark.id)}>
                    <Delete color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingId ? 'Edit Trademark' : 'Add New Trademark'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="nameArabic"
              label="Arabic Name"
              fullWidth
              value={formData.nameArabic}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={handleFileChange}
                required={!editingId}
              />
              <label htmlFor="image-upload">
                <Button 
                  variant="outlined" 
                  component="span" 
                  fullWidth 
                  sx={{ mt: 1 }}
                  startIcon={<AddIcon />}
                >
                  {selectedFile ? 'Change Image' : 'Upload Image'}
                </Button>
              </label>
              {selectedFile ? (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {selectedFile.name}
                </Typography>
              ) : formData.imageUrl ? (
                <Box mt={1}>
                  <Typography variant="caption" display="block">Current image:</Typography>
                  <Box 
                    component="img" 
                    src={resolveImageUrl(formData.imageUrl)} 
                    alt="Current" 
                    sx={{ maxWidth: '100%', maxHeight: 100, mt: 1 }}
                  />
                </Box>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            {isUploading && <CircularProgress size={24} sx={{ mr: 2 }} />}
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TrademarksPage;